<script setup>
import { VueFinalModal } from "vue-final-modal";
import { ref, computed, watch, watchEffect, nextTick, onMounted, onUnmounted } from "vue";

const props = defineProps({
  uslName: { type: String, default: "" },
  lawyerName: { type: String, default: "" },
  uslugaId: { type: Number, default: null },
  cityUrl: { type: String, default: "" },
  allUslugi: { type: Array, default: () => [] },
  allSecondUslugi: { type: Array, default: () => [] },
  mainUslugaId: { type: Number, default: null },
  pageLawyer: { type: Object, default: null },
  quizLawyers: { type: Array, default: () => [] },
  allServiceQuestions: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["close"]);

// Ограничиваем высоту модалки по visualViewport, чтобы клавиатура не выталкивала её за экран
const modalMaxHeight = ref('');
function updateVH() {
  const h = window.visualViewport?.height ?? window.innerHeight;
  modalMaxHeight.value = `${Math.floor(h * 0.92)}px`;
}
onMounted(() => {
  window.visualViewport?.addEventListener('resize', updateVH);
  window.visualViewport?.addEventListener('scroll', updateVH);
  updateVH();

  // Preload lawyer avatars so they appear instantly when step 6 opens
  const paths = [
    props.pageLawyer?.avatar_path,
    ...props.quizLawyers.map(l => l.avatar_path),
  ].filter(Boolean);
  paths.forEach(path => { new Image().src = avatarUrl(path); });
});
onUnmounted(() => {
  window.visualViewport?.removeEventListener('resize', updateVH);
  window.visualViewport?.removeEventListener('scroll', updateVH);
});

// ── Persistence ──────────────────────────────────────────
const STORAGE_KEY = `quiz_${props.uslugaId ?? 'g'}`;

function loadSaved() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// saved читаем в setup (в память) ДО watchEffect; на сервере localStorage нет → null
const saved = loadSaved();

// ── State ────────────────────────────────────────────────
// 0=service, 1=service-questions, 2-4=general questions, 5=description, 6=lawyer, 7=form, 8=chat+reg
// SSR: инициализируем ДЕФОЛТАМИ, чтобы серверный и клиентский первый рендер совпали.
// Восстановление сохранённого прогресса — в onMounted (после гидрации).
const step = ref(0);
const answers = ref({});
const serviceChecked = ref({});
const form = ref({
  name: "",
  phone: "",
  description: "",
  consent: false,
  lawyer_id: null,
  lawyer_name: "",
});
const sending = ref(false);
const error = ref("");

// ── Selected service (step 0) ────────────────────────────
const selectedUslName = ref(props.uslName);
const selectedUslugaId = ref(props.uslugaId);

// serviceSelectStep: null = confirm screen | 'main' = main list | 'second' = second list
const serviceSelectStep = ref(selectedUslName.value ? null : 'main');

// Восстанавливаем сохранённый прогресс квиза только на клиенте, после гидрации
onMounted(() => {
  if (!saved) return;
  if (saved.step != null) step.value = saved.step;
  if (saved.answers) answers.value = saved.answers;
  if (saved.serviceChecked) serviceChecked.value = saved.serviceChecked;
  if (saved.selectedUslName) selectedUslName.value = saved.selectedUslName;
  if (saved.selectedUslugaId != null) selectedUslugaId.value = saved.selectedUslugaId;
  if (saved.phone != null) form.value.phone = saved.phone;
  if (saved.description != null) form.value.description = saved.description;
  if (saved.consent != null) form.value.consent = saved.consent;
  if (saved.lawyer_id != null) form.value.lawyer_id = saved.lawyer_id;
  if (saved.lawyer_name != null) form.value.lawyer_name = saved.lawyer_name;
  serviceSelectStep.value = selectedUslName.value ? null : 'main';
});
const selectedMainUsluga = ref(null);
const effectiveMainId = ref(props.mainUslugaId);
const searchQuery = ref("");

watchEffect(() => {
  if (step.value === 8) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    step: step.value,
    answers: answers.value,
    serviceChecked: serviceChecked.value,
    selectedUslName: selectedUslName.value,
    selectedUslugaId: selectedUslugaId.value,
    phone: form.value.phone,
    description: form.value.description,
    consent: form.value.consent,
    lawyer_id: form.value.lawyer_id,
    lawyer_name: form.value.lawyer_name,
  }));
});

const filteredMainUslugi = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return props.allUslugi;
  return props.allUslugi.filter(u => u.usl_name.toLowerCase().includes(q));
});

const filteredSecondUslugi = computed(() => {
  if (!selectedMainUsluga.value) return [];
  return props.allSecondUslugi.filter(u => u.main_usluga_id === selectedMainUsluga.value.id);
});

function shortName(name) {
  const idx = name.indexOf(' - ');
  return idx !== -1 ? name.slice(0, idx) : name;
}

const totalSteps = 4; // steps 1-4
const progress = computed(() => step.value <= 0 ? 0 : Math.round((Math.min(step.value, totalSteps) / totalSteps) * 100));

const currentServiceQuestions = computed(() => {
  const key = String(selectedUslugaId.value ?? '');
  return props.allServiceQuestions?.[key] ?? [];
});

// ── Questions ────────────────────────────────────────────
const questions = [
  {
    id: "goal",
    text: "Что нужно сделать?",
    emoji: "🎯",
    options: [
      { value: "consult",  label: "Получить консультацию",     icon: "💬" },
      { value: "court",    label: "Разобраться в суде",        icon: "⚖️" },
      { value: "doc",      label: "Составить документ",        icon: "📄" },
      { value: "defense",  label: "Защититься от претензий",   icon: "🛡️" },
    ],
  },
  {
    id: "urgency",
    text: "Насколько срочно?",
    emoji: "⏱️",
    options: [
      { value: "today",  label: "Очень срочно — нужно сегодня", icon: "🔥" },
      { value: "week",   label: "В ближайшие несколько дней",   icon: "📅" },
      { value: "no_rush", label: "Не спешу, изучаю варианты",  icon: "🔍" },
    ],
  },
  {
    id: "format",
    text: "Как удобнее получить помощь?",
    emoji: "📞",
    options: [
      { value: "call",    label: "Позвоните мне",         icon: "📱" },
      { value: "chat",    label: "Напишите в мессенджер", icon: "💬" },
      { value: "office",  label: "Приеду в офис",         icon: "🏢" },
    ],
  },
];

// ── Reset on modal open ───────────────────────────────────
function onQuizOpen() {
  try { window.goal("QUIZ_START"); } catch {}
  resetState();
}

function resetState() {
  const s = loadSaved();
  const savedStep = s?.step ?? 0;
  step.value = savedStep;
  answers.value = s?.answers ?? {};
  selectedUslName.value = s?.selectedUslName ?? props.uslName;
  selectedUslugaId.value = s?.selectedUslugaId ?? props.uslugaId;
  form.value.phone = s?.phone ?? '';
  form.value.description = s?.description ?? '';
  form.value.consent = s?.consent ?? false;
  serviceChecked.value = s?.serviceChecked ?? {};
  serviceSelectStep.value = (savedStep === 0 && !selectedUslName.value) ? 'main' : null;
  selectedMainUsluga.value = null;
  effectiveMainId.value = props.mainUslugaId;
  error.value = '';
  chatPhase.value = 0;
  regEmail.value = '';
  regPassword.value = '';
  regError.value = '';
  regDone.value = false;
  showRegForm.value = false;
}

// ── Actions ──────────────────────────────────────────────
function pickAnswer(questionId, value) {
  error.value = "";
  answers.value[questionId] = value;
  step.value++;
}

function goAfterService() {
  serviceChecked.value = {};
  const hasQ = (props.allServiceQuestions?.[String(selectedUslugaId.value)] ?? []).length > 0;
  step.value = hasQ ? 1 : 2;
}

function confirmService() {
  if (!form.value.consent) {
    error.value = "Необходимо согласие на обработку данных";
    return;
  }
  error.value = "";
  const mainId = effectiveMainId.value;
  const mainService = mainId ? props.allUslugi.find(u => u.id === mainId) : null;
  const hasSeconds = mainService && props.allSecondUslugi.some(u => u.main_usluga_id === mainId);
  if (hasSeconds) {
    selectedMainUsluga.value = mainService;
    serviceSelectStep.value = 'second';
  } else {
    goAfterService();
  }
}

function pickMainUsluga(service) {
  selectedMainUsluga.value = service;
  effectiveMainId.value = service.id;
  selectedUslName.value = service.usl_name;
  selectedUslugaId.value = service.id;
  const hasSeconds = props.allSecondUslugi.some(u => u.main_usluga_id === service.id);
  serviceSelectStep.value = hasSeconds ? 'second' : null;
}

function pickSecondUsluga(service) {
  if (!form.value.consent) { error.value = "Необходимо согласие на обработку данных"; return; }
  error.value = "";
  selectedUslName.value = service.usl_name;
  selectedUslugaId.value = service.id;
  serviceSelectStep.value = null;
  goAfterService();
}

function pickOther() {
  if (!form.value.consent) { error.value = "Необходимо согласие на обработку данных"; return; }
  error.value = "";
  selectedUslName.value = selectedMainUsluga.value.usl_name;
  selectedUslugaId.value = selectedMainUsluga.value.id;
  serviceSelectStep.value = null;
  goAfterService();
}

function avatarUrl(path) {
  if (!path) return '/storage/default/avatar.webp';
  return path.startsWith('http') ? path : '/' + path;
}

function pickLawyer(lawyer) {
  form.value.lawyer_id = lawyer.id;
  form.value.lawyer_name = lawyer.name;
  step.value = 7;
}

function pickAllLawyers() {
  form.value.lawyer_id = null;
  form.value.lawyer_name = 'Команда юристов';
  step.value = 7;
}

function formatPhone(e) {
  const input = e.target;
  let digits = input.value.replace(/\D/g, '');
  if (digits.startsWith('7') || digits.startsWith('8')) digits = digits.slice(1);
  digits = digits.slice(0, 10);

  let result = '+7';
  if (digits.length > 0) result += ' (' + digits.slice(0, Math.min(3, digits.length));
  if (digits.length > 3)  result += ') ' + digits.slice(3, Math.min(6, digits.length));
  if (digits.length > 6)  result += '-' + digits.slice(6, Math.min(8, digits.length));
  if (digits.length > 8)  result += '-' + digits.slice(8, 10);

  form.value.phone = result;
  input.value = result;
  nextTick(() => input.setSelectionRange(result.length, result.length));
}

function back() {
  error.value = "";
  if (step.value === 1) {
    step.value = 0;
    serviceSelectStep.value = null;
  } else if (step.value === 2) {
    const hasQ = currentServiceQuestions.value.length > 0;
    if (hasQ) { step.value = 1; }
    else { step.value = 0; serviceSelectStep.value = null; }
  } else {
    step.value--;
  }
}

// ── Chat + Registration (step 7) ─────────────────────────
const chatPhase = ref(0); // 1=reading, 2=typing, 3=replied
const regEmail = ref('');
const regPassword = ref('');
const regSending = ref(false);
const regError = ref('');
const regDone = ref(false);
const showRegForm = ref(false);

const lawyerForChat = computed(() => {
  if (form.value.lawyer_id && props.pageLawyer?.id === form.value.lawyer_id) return props.pageLawyer;
  if (form.value.lawyer_id) return props.quizLawyers.find(l => l.id === form.value.lawyer_id) ?? props.pageLawyer;
  return props.quizLawyers[0] ?? props.pageLawyer;
});

const lawyerIsFemale = computed(() => !!lawyerForChat.value?.is_female);

const chatLawyers = computed(() => {
  if (!form.value.lawyer_id) return props.quizLawyers.slice(0, 3);
  const l = lawyerForChat.value;
  return l ? [l] : [];
});

const currentTime = computed(() => {
  const n = new Date();
  return String(n.getHours()).padStart(2, '0') + ':' + String(n.getMinutes()).padStart(2, '0');
});

watch(() => step.value, (val) => {
  if (val === 8) {
    chatPhase.value = 1;
    setTimeout(() => {
      chatPhase.value = 2;
      setTimeout(() => {
        chatPhase.value = 3;
        setTimeout(() => { chatPhase.value = 4; }, 2000);
      }, 2500);
    }, 2000);
  }
});

async function registerUser() {
  if (!regEmail.value || !regPassword.value) { regError.value = 'Заполните все поля'; return; }
  regSending.value = true;
  regError.value = '';
  try {
    const resp = await fetch('/api/quiz-register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': getCsrfToken(), 'Accept': 'application/json' },
      body: JSON.stringify({ email: regEmail.value, password: regPassword.value }),
    });
    const data = await resp.json().catch(() => ({}));
    if (resp.ok) {
      regDone.value = true;
      try { window.goal("QUIZ_REGISTER"); } catch {}
    } else if (resp.status === 422) {
      regError.value = data.message || Object.values(data.errors ?? {}).flat()[0] || 'Ошибка регистрации';
    } else if (resp.status === 419) {
      regError.value = 'Сессия истекла, обновите страницу';
    } else {
      regError.value = 'Ошибка регистрации, попробуйте ещё раз';
    }
  } catch {
    regError.value = 'Ошибка соединения';
  } finally {
    regSending.value = false;
  }
}

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta instanceof HTMLMetaElement ? meta.content : "";
}

async function submit() {
  if (form.value.phone.replace(/\D/g, '').length !== 11) {
    error.value = "Укажите номер телефона";
    return;
  }
  if (!form.value.consent) {
    error.value = "Необходимо согласие на обработку данных";
    return;
  }
  error.value = "";
  sending.value = true;
  try {
    const resp = await fetch("/api/quiz-lead", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": getCsrfToken() },
      body: JSON.stringify({
        name: form.value.name,
        phone: form.value.phone,
        usluga_id: selectedUslugaId.value,
        usl_name: selectedUslName.value,
        answers: answers.value,
        service_answers: currentServiceQuestions.value
          .filter(q => serviceChecked.value[String(q.id)])
          .map(q => q.question),
        description: form.value.description,
        lawyer_id: form.value.lawyer_id,
        lawyer_name: form.value.lawyer_name,
        city: props.cityUrl,
        page_url: window.location.href,
      }),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      console.error('[Quiz] submit failed', resp.status, text.slice(0, 300));
      throw new Error(`HTTP ${resp.status}`);
    }
    try { window.goal("QUIZ_LEAD"); } catch {}
    localStorage.removeItem(STORAGE_KEY);
    step.value = 8;
  } catch (e) {
    console.error('[Quiz] submit error', e);
    error.value = "Ошибка отправки, попробуйте позвонить" + (e?.message ? ` (${e.message})` : '');
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <VueFinalModal
    class="flex items-end sm:items-center justify-center"
    content-class="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-xl flex flex-col overflow-hidden"
    :content-style="modalMaxHeight ? { maxHeight: modalMaxHeight } : {}"
    overlay-transition="vfm-fade"
    content-transition="vfm-slide-down"
    :click-to-close="step !== 8"
    :esc-to-close="step !== 8"
    @before-open="onQuizOpen"
    @closed="emit('close')"
  >
    <!-- Progress bar (не скроллируется) -->
    <div v-if="step >= 1 && step <= 4" class="h-1 bg-gray-100 w-full flex-shrink-0">
      <div class="h-1 bg-blue-500 transition-all duration-500" :style="{ width: progress + '%' }"></div>
    </div>

    <!-- Close -->
    <button
      v-if="step !== 8"
      type="button"
      @click="emit('close')"
      class="absolute top-4 right-4 z-10 text-gray-500 hover:text-gray-600 transition-colors"
    >
      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>

    <!-- Скроллируемая область — не растягивается при появлении клавиатуры -->
    <div class="flex-1 overflow-y-auto overscroll-contain">
    <div class="p-6">

      <!-- ── Step 0: Service confirm ── -->
      <div v-if="step === 0">
        <div class="text-center mb-4">
          <div class="text-3xl mb-2">⚖️</div>
          <h2 class="text-lg font-bold text-gray-900 mb-1">Подберём решение для вас</h2>
        </div>

        <!-- Confirm current service -->
        <template v-if="serviceSelectStep === null">
          <p class="text-sm text-gray-500 mb-2">Вы интересуетесь:</p>
          <div class="flex items-center justify-between px-4 py-3.5 rounded-xl border-2 border-blue-400 bg-blue-50 mb-4">
            <span class="text-sm font-semibold text-blue-700">{{ shortName(selectedUslName) }}</span>
            <svg class="w-5 h-5 text-blue-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <label class="flex items-start gap-2 cursor-pointer mb-2">
            <input v-model="form.consent" type="checkbox" class="mt-0.5 w-4 h-4 shrink-0 accent-blue-600 cursor-pointer"/>
            <span class="text-xs text-gray-500 leading-relaxed">
              Согласен(а) на обработку персональных данных —
              <a href="https://nedicom.ru/personal" target="_blank" class="text-blue-500 hover:underline">политика конфиденциальности</a>
            </span>
          </label>
          <p v-if="error" class="text-xs text-red-500 mb-2 pl-6">{{ error }}</p>
          <button type="button" @click="confirmService"
            class="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors text-sm mb-3">
            Да, верно — продолжить
          </button>
          <button type="button" @click="serviceSelectStep = 'main'; searchQuery = ''"
            class="w-full py-2.5 text-sm text-gray-500 hover:text-gray-600 transition-colors">
            Нет, интересует другая услуга
          </button>
        </template>

        <!-- Main services list -->
        <template v-else-if="serviceSelectStep === 'main'">
          <input v-model="searchQuery" type="text" placeholder="Поиск услуги..."
            style="font-size: 16px;"
            class="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition mb-2"/>
          <div class="flex flex-col gap-1.5 max-h-64 overflow-y-auto mb-3">
            <button v-for="s in filteredMainUslugi" :key="s.id" type="button" @click="pickMainUsluga(s)"
              class="flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all text-sm font-medium text-gray-800 text-left">
              <span>{{ shortName(s.usl_name) }}</span>
              <svg class="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
          <button v-if="selectedUslName" type="button" @click="serviceSelectStep = null"
            class="w-full py-2.5 text-sm text-gray-500 hover:text-gray-600 transition-colors">
            ← Вернуться
          </button>
        </template>

        <!-- Second services list -->
        <template v-else-if="serviceSelectStep === 'second'">
          <button type="button" @click="serviceSelectStep = 'main'; searchQuery = ''"
            class="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-600 transition-colors mb-3">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            {{ shortName(selectedMainUsluga.usl_name) }}
          </button>
          <div class="flex flex-col gap-1.5 max-h-64 overflow-y-auto mb-3">
            <button v-for="s in filteredSecondUslugi" :key="s.id" type="button" @click="pickSecondUsluga(s)"
              class="flex items-center px-4 py-3 rounded-xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all text-sm font-medium text-gray-800 text-left">
              {{ shortName(s.usl_name) }}
            </button>
            <button type="button" @click="pickOther()"
              class="flex items-center px-4 py-3 rounded-xl border border-gray-100 hover:border-blue-300 hover:bg-blue-50 transition-all text-sm font-medium text-gray-500 text-left">
              Другое
            </button>
          </div>
          <label class="flex items-start gap-2 cursor-pointer mb-2">
            <input v-model="form.consent" type="checkbox" class="mt-0.5 w-4 h-4 shrink-0 accent-blue-600 cursor-pointer"/>
            <span class="text-xs text-gray-500 leading-relaxed">
              Согласен(а) на обработку персональных данных —
              <a href="https://nedicom.ru/personal" target="_blank" class="text-blue-500 hover:underline">политика конфиденциальности</a>
            </span>
          </label>
          <p v-if="error" class="text-xs text-red-500 mb-1 pl-6">{{ error }}</p>
        </template>
      </div>

      <!-- ── Step 1: Service-specific questions (checkboxes) ── -->
      <div v-if="step === 1">
        <div class="flex items-center gap-2 mb-5">
          <button type="button" @click="back" class="text-gray-500 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <span class="text-xs text-gray-500 ml-auto">1 из {{ totalSteps }}</span>
        </div>
        <div class="text-center mb-5">
          <div class="text-3xl mb-2">📋</div>
          <h2 class="text-lg font-bold text-gray-900">Что касается вашей ситуации?</h2>
          <p class="text-xs text-gray-500 mt-1">Отметьте всё подходящее</p>
        </div>
        <div class="flex flex-col gap-2 mb-5 max-h-64 overflow-y-auto pr-1">
          <label
            v-for="q in currentServiceQuestions" :key="q.id"
            class="flex items-start gap-3 px-4 py-3 rounded-xl border-2 cursor-pointer transition-all"
            :class="serviceChecked[String(q.id)] ? 'border-blue-400 bg-blue-50' : 'border-gray-100 hover:border-gray-200'"
          >
            <input type="checkbox" :value="true" v-model="serviceChecked[String(q.id)]"
              class="mt-0.5 w-4 h-4 shrink-0 accent-blue-600 cursor-pointer"/>
            <span class="text-sm text-gray-800 leading-snug">{{ q.question }}</span>
          </label>
        </div>
        <button type="button" @click="step = 2"
          class="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors text-sm">
          Продолжить
        </button>
      </div>

      <!-- ── Steps 2–4: General questions ── -->
      <div v-if="step >= 2 && step <= 4">
        <!-- Back + counter -->
        <div class="flex items-center gap-2 mb-5">
          <button type="button" @click="back" class="text-gray-500 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <span class="text-xs text-gray-500 ml-auto">{{ step }} из {{ totalSteps }}</span>
        </div>

        <div class="text-center mb-6">
          <div class="text-3xl mb-2">{{ questions[step - 2].emoji }}</div>
          <h2 class="text-lg font-bold text-gray-900">{{ questions[step - 2].text }}</h2>
        </div>

        <div class="flex flex-col gap-2.5">
          <button
            v-for="opt in questions[step - 2].options"
            :key="opt.value"
            type="button"
            @click="pickAnswer(questions[step - 2].id, opt.value)"
            class="flex items-center gap-3 w-full px-4 py-3.5 text-left rounded-xl border-2 border-gray-100
                   hover:border-blue-400 hover:bg-blue-50 transition-all duration-150 group"
          >
            <span class="text-xl shrink-0">{{ opt.icon }}</span>
            <span class="text-sm font-semibold text-gray-800 group-hover:text-blue-700">{{ opt.label }}</span>
          </button>
        </div>

      </div>

      <!-- ── Step 5: Description ── -->
      <div v-else-if="step === 5">
        <div class="flex items-center gap-2 mb-5">
          <button type="button" @click="back" class="text-gray-500 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        </div>
        <div class="text-center mb-6">
          <div class="text-3xl mb-2">📝</div>
          <h2 class="text-lg font-bold text-gray-900">Опишите вашу ситуацию</h2>
          <p class="text-sm text-gray-500 mt-1">Пара слов поможет юристу подготовиться заранее</p>
        </div>
        <textarea
          v-model="form.description"
          rows="4"
          placeholder="Например: нужно оспорить завещание, есть другие наследники..."
          style="font-size: 16px;"
          class="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition resize-none mb-4"
        ></textarea>
        <button
          type="button"
          @click="step = 6"
          class="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors text-sm mb-2"
        >
          Продолжить
        </button>
        <button
          type="button"
          @click="step = 6"
          class="w-full py-2.5 text-sm text-gray-500 hover:text-gray-600 transition-colors"
        >
          Пропустить
        </button>
      </div>

      <!-- ── Step 6: Lawyer select ── -->
      <div v-else-if="step === 6">
        <div class="flex items-center gap-2 mb-5">
          <button type="button" @click="back" class="text-gray-500 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        </div>
        <div class="text-center mb-5">
          <div class="text-3xl mb-2">👤</div>
          <h2 class="text-lg font-bold text-gray-900">К кому хотите обратиться?</h2>
        </div>

        <!-- Page lawyer (author of service) -->
        <button v-if="pageLawyer" type="button" @click="pickLawyer(pageLawyer)"
          class="w-full flex items-center gap-4 px-4 py-4 rounded-xl border-2 border-gray-100 hover:border-blue-400 hover:bg-blue-50 transition-all mb-3 text-left">
          <img :src="avatarUrl(pageLawyer.avatar_path)" :alt="pageLawyer.name"
            class="w-14 h-14 rounded-full object-cover shrink-0 border border-gray-200"
            @error="e => e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 56 56%22%3E%3Crect width=%2256%22 height=%2256%22 fill=%22%23e5e7eb%22/%3E%3Ccircle cx=%2228%22 cy=%2222%22 r=%228%22 fill=%22%239ca3af%22/%3E%3Cellipse cx=%2228%22 cy=%2242%22 rx=%2214%22 ry=%228%22 fill=%22%239ca3af%22/%3E%3C/svg%3E'"/>
          <div class="min-w-0">
            <div class="text-sm font-bold text-gray-900 leading-tight">{{ pageLawyer.name }}</div>
            <div class="text-xs text-gray-500 mt-0.5">Автор услуги</div>
            <div v-if="pageLawyer.expirience" class="text-xs text-gray-500">Стаж {{ pageLawyer.expirience }} лет</div>
          </div>
          <svg class="w-5 h-5 text-gray-300 ml-auto shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>

        <!-- Team lawyers -->
        <div v-if="quizLawyers.length" class="mb-1">
          <p class="text-xs text-gray-500 mb-2 text-center">или обратиться к команде юристов</p>
          <div class="flex flex-wrap gap-3 justify-center mb-3">
            <button v-for="l in quizLawyers.filter(l => l.id !== pageLawyer?.id)" :key="l.id" type="button" @click="pickLawyer(l)"
              :title="l.name"
              class="flex flex-col items-center gap-1 group">
              <img :src="avatarUrl(l.avatar_path)" :alt="l.name"
                class="w-12 h-12 rounded-full object-cover border-2 border-gray-200 group-hover:border-blue-400 transition-colors"
                @error="e => e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 48 48%22%3E%3Crect width=%2248%22 height=%2248%22 fill=%22%23e5e7eb%22/%3E%3Ccircle cx=%2224%22 cy=%2219%22 r=%227%22 fill=%22%239ca3af%22/%3E%3Cellipse cx=%2224%22 cy=%2237%22 rx=%2212%22 ry=%227%22 fill=%22%239ca3af%22/%3E%3C/svg%3E'"/>
              <span class="text-xs text-gray-500 group-hover:text-blue-600 max-w-[64px] text-center leading-tight">
                {{ l.name.split(' ')[0] }}
              </span>
            </button>
          </div>
          <button type="button" @click="pickAllLawyers"
            class="w-full py-2.5 text-sm text-gray-500 hover:text-gray-600 transition-colors text-center">
            Обратиться ко всем сразу
          </button>
        </div>
      </div>

      <!-- ── Step 7: Contact form ── -->
      <div v-else-if="step === 7">
        <div class="flex items-center gap-2 mb-5">
          <button type="button" @click="back" class="text-gray-500 hover:text-gray-600 transition-colors">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
        </div>
        <div class="text-center mb-6">
          <div class="text-3xl mb-2">🙌</div>
          <h2 class="text-lg font-bold text-gray-900">Отлично! Осталось оставить контакт</h2>
          <p class="text-sm text-gray-500 mt-1">
            Примерное время ответа — 15 минут
          </p>
        </div>

        <div class="mb-4">
          <input
            :value="form.phone"
            @input="formatPhone"
            type="tel"
            maxlength="18"
            placeholder="+7 (___) ___-__-__"
            style="font-size: 16px;"
            class="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"
          />
          <p v-if="error" class="text-xs text-red-500 mt-1.5 pl-1">{{ error }}</p>
        </div>

        <button
          type="button"
          @click="submit"
          :disabled="sending || form.phone.replace(/\D/g, '').length !== 11"
          class="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-blue-400 text-white font-bold rounded-xl transition-colors text-sm"
        >
          {{ sending ? "Отправляем..." : "Получить консультацию" }}
        </button>

        <p class="text-center text-xs text-gray-500 mt-3">
          Первая консультация — бесплатно
        </p>
      </div>

      <!-- ── Step 8: Chat + Registration ── -->
      <div v-else-if="step === 8">

        <!-- Lawyer / team header -->
        <div class="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
          <div class="flex -space-x-2 shrink-0">
            <img v-for="l in chatLawyers" :key="l.id"
              :src="avatarUrl(l.avatar_path)" :alt="l.name"
              class="w-9 h-9 rounded-full object-cover border-2 border-white"
              @error="e => e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 36 36%22%3E%3Crect width=%2236%22 height=%2236%22 fill=%22%23e5e7eb%22/%3E%3Ccircle cx=%2218%22 cy=%2214%22 r=%226%22 fill=%22%239ca3af%22/%3E%3Cellipse cx=%2218%22 cy=%2230%22 rx=%2210%22 ry=%226%22 fill=%22%239ca3af%22/%3E%3C/svg%3E'"/>
          </div>
          <div class="min-w-0">
            <div class="text-sm font-semibold text-gray-900 truncate">{{ form.lawyer_name || 'Команда юристов' }}</div>
            <div class="flex items-center gap-1">
              <div class="w-1.5 h-1.5 rounded-full bg-green-400"></div>
              <span class="text-xs text-gray-500">онлайн</span>
            </div>
          </div>
        </div>

        <!-- Chat messages -->
        <div class="flex flex-col gap-3 mb-5 min-h-[90px]">

          <!-- User bubble -->
          <div class="flex justify-end">
            <div class="bg-blue-600 text-white text-sm px-3.5 py-2 rounded-2xl rounded-br-sm max-w-[72%]">
              {{ form.phone }}
              <div class="text-xs opacity-60 mt-0.5 text-right">{{ currentTime }} ✓✓</div>
            </div>
          </div>

          <!-- Lawyer response -->
          <div v-if="chatPhase >= 1" class="flex items-end gap-2">
            <img :src="avatarUrl(lawyerForChat?.avatar_path)"
              class="w-7 h-7 rounded-full object-cover border border-gray-200 shrink-0"
              @error="e => e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 28 28%22%3E%3Crect width=%2228%22 height=%2228%22 fill=%22%23e5e7eb%22/%3E%3Ccircle cx=%2214%22 cy=%2211%22 r=%225%22 fill=%22%239ca3af%22/%3E%3Cellipse cx=%2214%22 cy=%2224%22 rx=%228%22 ry=%225%22 fill=%22%239ca3af%22/%3E%3C/svg%3E'"/>
            <div>
              <p v-if="chatPhase === 1" class="text-xs text-gray-500 italic mb-1">читает...</p>
              <div v-else-if="chatPhase === 2"
                class="flex gap-1 items-center px-3.5 py-2.5 bg-gray-100 rounded-2xl rounded-bl-sm w-16">
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                <span class="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
              </div>
              <div v-else-if="chatPhase >= 3"
                class="bg-gray-100 text-gray-800 text-sm px-3.5 py-2 rounded-2xl rounded-bl-sm max-w-[72%]">
                {{ lawyerIsFemale ? 'Получила вашу заявку.' : 'Получил вашу заявку.' }}
                <div class="text-xs text-gray-500 mt-0.5">{{ currentTime }}</div>
              </div>
            </div>
          </div>

          <!-- Bot message -->
          <transition name="quiz-fade">
            <div v-if="chatPhase >= 4" class="flex items-end gap-2">
              <div class="w-7 h-7 rounded-full bg-blue-100 border border-blue-200 shrink-0 flex items-center justify-center text-sm">
                🤖
              </div>
              <div>
                <p class="text-xs text-gray-500 mb-1">Бот</p>
                <div class="bg-gray-100 text-gray-800 text-sm px-3.5 py-2 rounded-2xl rounded-bl-sm max-w-[72%]">
                  {{ lawyerIsFemale ? 'Указала что перезвонит в ближайшее время 📞' : 'Указал что перезвонит в ближайшее время 📞' }}
                  <div class="text-xs text-gray-500 mt-0.5">{{ currentTime }}</div>
                </div>
              </div>
            </div>
          </transition>

        </div>

        <!-- Registration prompt -->
        <transition name="quiz-fade">
          <div v-if="chatPhase >= 4" class="border-t border-gray-100 pt-4">

            <template v-if="!regDone">
              <p class="text-sm font-semibold text-gray-800 text-center mb-0.5">Сохраните историю обращения</p>
              <p class="text-xs text-gray-500 text-center mb-4">и получайте письменные ответы юристов</p>

              <!-- Yandex -->
              <a href="/login" target="_blank" rel="noopener"
                class="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#FFCC00] hover:bg-yellow-400 text-gray-900 font-semibold text-sm rounded-xl transition-colors mb-3">
                <span class="text-base font-black leading-none" style="color:#FC3F1D">Я</span>
                Войти через Яндекс
              </a>

              <div class="flex items-center gap-2 mb-3">
                <div class="flex-1 h-px bg-gray-200"></div>
                <span class="text-xs text-gray-500">или</span>
                <div class="flex-1 h-px bg-gray-200"></div>
              </div>

              <template v-if="!showRegForm">
                <button type="button" @click="showRegForm = true"
                  class="w-full py-2.5 text-sm text-gray-600 border border-gray-200 hover:border-gray-300 rounded-xl transition-colors">
                  Зарегистрироваться по email
                </button>
              </template>
              <template v-else>
                <div class="flex flex-col gap-2">
                  <input v-model="regEmail" type="email" placeholder="Email" autocomplete="email"
                    style="font-size: 16px;"
                    class="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"/>
                  <input v-model="regPassword" type="password" placeholder="Пароль" autocomplete="new-password"
                    style="font-size: 16px;"
                    class="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition"/>
                  <p v-if="regError" class="text-xs text-red-500 pl-1">{{ regError }}</p>
                  <button type="button" @click="registerUser" :disabled="regSending"
                    class="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-blue-400 text-white font-bold rounded-xl transition-colors text-sm">
                    {{ regSending ? 'Регистрируем...' : 'Зарегистрироваться' }}
                  </button>
                </div>
              </template>

              <button type="button" @click="emit('close')"
                class="block mx-auto mt-3 text-xs text-gray-500 hover:text-gray-600">
                Закрыть
              </button>
            </template>

            <!-- After registration -->
            <template v-else>
              <div class="text-center py-2">
                <div class="text-4xl mb-2">✅</div>
                <p class="font-bold text-gray-900 mb-1">Аккаунт создан!</p>
                <p class="text-sm text-gray-500 mb-4">Теперь вы получите письменный ответ юриста</p>
                <button type="button" @click="emit('close')"
                  class="text-xs text-gray-500 hover:text-gray-600">Закрыть</button>
              </div>
            </template>

          </div>
        </transition>

      </div>

    </div>
    </div><!-- /overflow-y-auto -->
  </VueFinalModal>
</template>

<style scoped>
.quiz-fade-enter-active { transition: opacity 0.5s ease, transform 0.5s ease; }
.quiz-fade-enter-from  { opacity: 0; transform: translateY(8px); }
</style>
